import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { DATE_FORMAT_RU, relevanceOptions } from 'constants/date';
import { geoconfirmedLayers, MAP_LAYERS_NAMES } from 'constants/mapTypes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { MapTypes } from 'interfaces';
import { settingsActions } from 'store/slices/mapV2/mapReducer/settingsSlice';
import { typesSelector } from 'store/slices/mapV2/mapReducer/settingsSlice/selectors';
import { timelineTypesSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/selectors';
import { operationalActions } from 'store/slices/mapV2/tabsReducer/operationalSlice';
import {
  availableRangesSelector,
  rangeSelector,
} from 'store/slices/mapV2/tabsReducer/operationalSlice/selectors';
import { TDateRange } from 'types';

import { TimelineTooltip } from 'components/Map/TimelineTooltip';
import TreeSelectWithDatepicker from 'components/ui/TreeSelectWithDatepicker';
import {
  getDateRangeFromRelevance,
  getDisplayRelevanceOrDate,
  useLocalStorageDates,
} from 'utils';
import { useOperationalLocalStorage } from 'utils/localStorage';

import { LineTextSwitch } from '../../LineTextSwitch';

import styles from './operational.module.scss';

export const Settings = () => {
  const [storedSettings, setStoredSettings] = useOperationalLocalStorage();

  const mapTypes = useAppSelector(typesSelector);
  const gcRanges = useAppSelector(availableRangesSelector);
  const operationalRange = useAppSelector(rangeSelector);
  const { operational: isTimeline } = useAppSelector(timelineTypesSelector);
  const [relevance, setRelevance] = useState(storedSettings.relevance);
  const [isSelectorDatepickerVisible, setIsSelectorDatepickerVisible] =
    useState(false);
  const datepickerRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  const [startDate, endDate] = operationalRange;
  const localStorageDates = useLocalStorageDates(
    storedSettings,
    operationalRange
  );

  const operationalMapTypes: MapTypes = useMemo(
    () =>
      geoconfirmedLayers.reduce(
        (acc, curr) => ({ ...acc, [curr.name]: mapTypes[curr.name] }),
        {}
      ),
    [mapTypes]
  );

  const areOperationalMapTypesSelected = useMemo(
    () => Object.values(operationalMapTypes).every((type) => type.active),
    [operationalMapTypes]
  );

  const handleSetOperationalMapType = useCallback(
    (type: string) => () => {
      dispatch(settingsActions.setTypeActive(type));
      setStoredSettings({
        ...storedSettings,
        [type]: !mapTypes[type].active,
      });
    },
    [mapTypes, dispatch]
  );

  const handleSetOperationalMapTypes = useCallback(() => {
    if (isTimeline) return;
    const updatedStoredSettings: Record<string, boolean> = {};

    geoconfirmedLayers.forEach((layer) => {
      dispatch(
        settingsActions.updateType({
          type: layer.name,
          field: 'active',
          value: !areOperationalMapTypesSelected,
        })
      );

      updatedStoredSettings[layer.name] = !areOperationalMapTypesSelected;
    });

    setStoredSettings({ ...storedSettings, ...updatedStoredSettings });
  }, [storedSettings, areOperationalMapTypesSelected, dispatch]);

  const handleSetOperationalRange = useCallback(
    (payload: TDateRange) => {
      const [payloadStartDate, payloadEndDate] = payload;
      dispatch(operationalActions.setRange([payloadStartDate, payloadEndDate]));
      if (relevance === 'other') {
        setStoredSettings({
          ...storedSettings,
          startDate: payloadStartDate,
          endDate: payloadEndDate,
        });

        if (payloadStartDate && payloadEndDate) {
          setIsSelectorDatepickerVisible(false);
        }
      }
    },
    [relevance, storedSettings]
  );

  const handleCalendarClose = () => {
    if (startDate && !endDate) {
      handleSetOperationalRange([startDate, startDate]);
    }
  };

  const relevanceDisplayValue = useMemo(
    () =>
      getDisplayRelevanceOrDate(relevance, startDate, endDate, DATE_FORMAT_RU),
    [relevance, startDate, endDate]
  );

  const handleSetRelevance = (v: string) => {
    setRelevance(v);
    setIsSelectorDatepickerVisible(v === 'other');
    setStoredSettings({
      ...storedSettings,
      relevance: v,
    });
  };

  useEffect(() => {
    if (
      operationalMapTypes[MAP_LAYERS_NAMES.gcFrontline].active !==
      storedSettings.gcFrontline
    )
      dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcFrontline));

    if (
      operationalMapTypes[MAP_LAYERS_NAMES.gcPlacemarks].active !==
      storedSettings.gcPlacemarks
    )
      dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcPlacemarks));

    if (
      operationalMapTypes[MAP_LAYERS_NAMES.fire].active !== storedSettings.fire
    )
      dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.fire));

    dispatch(operationalActions.setRange(localStorageDates));
  }, [
    storedSettings.fire,
    storedSettings.gcFrontline,
    storedSettings.gcPlacemarks,
  ]);

  useEffect(() => {
    const dateRange = getDateRangeFromRelevance(
      relevance,
      Boolean(storedSettings.startDate && storedSettings.endDate)
    );

    if (dateRange) {
      handleSetOperationalRange(dateRange);
    } else {
      handleSetOperationalRange(localStorageDates);
    }
  }, [
    relevance,
    storedSettings.startDate,
    storedSettings.endDate,
    storedSettings.fire,
    storedSettings.gcFrontline,
    storedSettings.gcPlacemarks,
  ]);

  useEffect(
    () => setRelevance(storedSettings.relevance),
    [storedSettings.relevance]
  );

  useEffect(() => {
    isTimeline && setIsSelectorDatepickerVisible(false);
  }, [isTimeline]);

  return (
    <div className="flex flex-col gap-4 p-4 overflow-auto">
      <div className="flex flex-col gap-4">
        <TimelineTooltip isTimeline={isTimeline}>
          <div>
            <LineTextSwitch
              active={areOperationalMapTypesSelected}
              text="Оперативная ситуация"
              classNames={{ text: 'tpg-b2' }}
              onSwitch={handleSetOperationalMapTypes}
              disabled={isTimeline}
            />
          </div>
        </TimelineTooltip>
        <div>
          <div className="flex flex-row justify-between text-tpg_title">
            <p className="tpg-c1">Актуальность</p>
            <div
              className={cn(
                'tpg-c2 hover:cursor-pointer text-bright_product',
                {
                  'hover:text-dark_product':
                    !isTimeline && !isSelectorDatepickerVisible,
                },
                { 'text-dark_product': isSelectorDatepickerVisible },
                { 'text-tpg_light': isTimeline }
              )}
              onClick={() =>
                !isTimeline &&
                setIsSelectorDatepickerVisible(!isSelectorDatepickerVisible)
              }
            >
              <TimelineTooltip isTimeline={isTimeline}>
                <span>{relevanceDisplayValue}</span>
              </TimelineTooltip>
            </div>
          </div>
          <div className="absolute z-[1]" ref={datepickerRef}>
            {isSelectorDatepickerVisible && !isTimeline && (
              <TreeSelectWithDatepicker
                datepickerValue="other"
                selectorValue={relevance}
                selectorOptions={relevanceOptions}
                onSelect={handleSetRelevance}
                minDate={gcRanges?.frontline_min}
                maxDate={gcRanges?.frontline_max}
                startDate={startDate}
                endDate={endDate}
                setDate={handleSetOperationalRange}
                onCalendarClose={handleCalendarClose}
                visibleBackPropagate={setIsSelectorDatepickerVisible}
                menuPlacement="bottom"
                optionsClassName={styles['operational-selector']}
                datepickerClassName={styles['operational-datepicker']}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {geoconfirmedLayers.map((item, index) => (
          <LineTextSwitch
            key={index}
            active={operationalMapTypes[item.name].active}
            text={item.label}
            classNames={{ text: 'tpg-c1' }}
            onSwitch={handleSetOperationalMapType(item.name)}
          />
        ))}
      </div>
    </div>
  );
};
